import { useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TackLogo } from "@/components/TackLogo";
import { SiteFooter } from "@/components/SiteFooter";
import { Sentry, isSentryConfigured, isSentryInitialized } from "@/lib/sentry";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/status")({
  head: () => ({
    meta: [
      { title: "Tack. System status" },
      {
        name: "description",
        content:
          "Live operational status and 30-day uptime history for Tack — the QR-based customer retention platform.",
      },
      { property: "og:title", content: "Tack. System status" },
      {
        property: "og:description",
        content: "Live operational status and 30-day uptime history for Tack.",
      },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/status" }],
  }),
  component: StatusPage,
});

type LiveHealth = {
  status: "ok" | "degraded" | "down";
  db: "ok" | "error";
  service_key?: "ok" | "missing";
  latency_ms: number;
  timestamp: string;
  error?: string;
};

type HealthCheckRow = {
  checked_at: string;
  status: "ok" | "degraded" | "down";
  db_ok: boolean;
  latency_ms: number | null;
};

type ErrorRow = { function_name: string; created_at: string };

// ---------- data fetching ----------

async function fetchLiveHealth(): Promise<LiveHealth> {
  const res = await fetch("/api/public/health", { cache: "no-store" });
  // Even on 503 the body is valid JSON we want to render.
  const body = (await res.json()) as LiveHealth;
  return body;
}

async function fetchHistory(): Promise<HealthCheckRow[]> {
  // Reads via a SECURITY DEFINER RPC that exposes only non-sensitive columns.
  // Direct SELECT on public.health_checks is restricted to admins.
  const { data, error } = await supabase.rpc("get_public_health_history", {
    _days: 30,
  });
  if (error) throw error;
  return (data ?? []) as HealthCheckRow[];
}

async function fetchRecentErrors(): Promise<ErrorRow[]> {
  // Last 15 min of edge errors — used to flag redemption / payment components.
  const since = new Date(Date.now() - 15 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from("edge_function_errors")
    .select("function_name, created_at")
    .gte("created_at", since)
    .in("function_name", ["activate-redeem", "payments-webhook"])
    .limit(50);
  if (error) {
    // Non-fatal: status page should still render even if this query fails.
    return [];
  }
  return (data ?? []) as ErrorRow[];
}

// Minimum recorded checks before we trust the uptime % / incident view.
// At 1 check / 5 min, this is ~2 hours of history.
const MIN_CHECKS_FOR_STATS = 24;

// ---------- helpers ----------

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  return `${days}d ago`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

type DayBucket = {
  dayStart: Date;
  total: number;
  bad: number;
  hasData: boolean;
};

function bucketByDay(rows: HealthCheckRow[]): DayBucket[] {
  const buckets: DayBucket[] = [];
  const todayUtc = new Date();
  todayUtc.setUTCHours(0, 0, 0, 0);

  for (let i = 29; i >= 0; i--) {
    const d = new Date(todayUtc);
    d.setUTCDate(d.getUTCDate() - i);
    buckets.push({ dayStart: d, total: 0, bad: 0, hasData: false });
  }

  for (const row of rows) {
    const t = new Date(row.checked_at);
    const dayKey = new Date(t);
    dayKey.setUTCHours(0, 0, 0, 0);
    const idx = buckets.findIndex(
      (b) => b.dayStart.getTime() === dayKey.getTime(),
    );
    if (idx === -1) continue;
    buckets[idx].total += 1;
    buckets[idx].hasData = true;
    if (row.status !== "ok") buckets[idx].bad += 1;
  }

  return buckets;
}

type Incident = { startedAt: Date; endedAt: Date; durationMin: number };

// An incident = a contiguous run of non-ok rows of length >= 2 (i.e. ~10 min).
function detectIncidents(rows: HealthCheckRow[]): Incident[] {
  const incidents: Incident[] = [];
  let runStart: Date | null = null;
  let runEnd: Date | null = null;
  let runLen = 0;

  for (const row of rows) {
    const t = new Date(row.checked_at);
    if (row.status !== "ok") {
      if (!runStart) runStart = t;
      runEnd = t;
      runLen += 1;
    } else {
      if (runStart && runEnd && runLen >= 2) {
        const durMin = Math.max(
          5,
          Math.round((runEnd.getTime() - runStart.getTime()) / 60000) + 5,
        );
        incidents.push({
          startedAt: runStart,
          endedAt: runEnd,
          durationMin: durMin,
        });
      }
      runStart = null;
      runEnd = null;
      runLen = 0;
    }
  }
  if (runStart && runEnd && runLen >= 2) {
    const durMin = Math.max(
      5,
      Math.round((runEnd.getTime() - runStart.getTime()) / 60000) + 5,
    );
    incidents.push({
      startedAt: runStart,
      endedAt: runEnd,
      durationMin: durMin,
    });
  }

  // Most recent first, cap at 5.
  return incidents.reverse().slice(0, 5);
}

function calcUptime(rows: HealthCheckRow[]): number | null {
  if (rows.length === 0) return null;
  const ok = rows.filter((r) => r.status === "ok").length;
  return (ok / rows.length) * 100;
}

// ---------- UI ----------

type Tone = "ok" | "degraded" | "down" | "unknown";

function StatusDot({ tone }: { tone: Tone }) {
  const color =
    tone === "ok"
      ? "bg-emerald-500"
      : tone === "degraded"
        ? "bg-amber-500"
        : tone === "down"
          ? "bg-destructive"
          : "bg-muted-foreground/40";
  return (
    <span
      className={cn(
        "inline-block h-2.5 w-2.5 rounded-full shadow-[0_0_0_3px_hsl(var(--background))]",
        color,
        tone === "ok" && "animate-pulse",
      )}
      aria-hidden
    />
  );
}

function ComponentRow({
  label,
  tone,
  detail,
}: {
  label: string;
  tone: Tone;
  detail: string;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border/60 last:border-0">
      <span className="text-sm text-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">{detail}</span>
        <StatusDot tone={tone} />
      </div>
    </div>
  );
}

function StatusPage() {
  const liveQuery = useQuery({
    queryKey: ["status", "live"],
    queryFn: fetchLiveHealth,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });

  const historyQuery = useQuery({
    queryKey: ["status", "history"],
    queryFn: fetchHistory,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const errorsQuery = useQuery({
    queryKey: ["status", "recent-errors"],
    queryFn: fetchRecentErrors,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const live = liveQuery.data;
  const history = historyQuery.data ?? [];
  const recentErrors = errorsQuery.data ?? [];

  const dayBuckets = useMemo(() => bucketByDay(history), [history]);
  const hasEnoughHistory = history.length >= MIN_CHECKS_FOR_STATS;
  // Suppress incidents when live is healthy and we don't yet have meaningful
  // history — avoids "ticking" fake incidents from setup/probe artefacts.
  const liveIsOk = live?.status === "ok";
  const rawIncidents = useMemo(() => detectIncidents(history), [history]);
  const incidents = hasEnoughHistory && !liveIsOk ? rawIncidents : hasEnoughHistory ? rawIncidents : [];
  const uptime = useMemo(
    () => (hasEnoughHistory ? calcUptime(history) : null),
    [history, hasEnoughHistory],
  );

  // Component-level tones derived from live + recent errors.
  const dbTone: Tone = live ? (live.db === "ok" ? "ok" : "down") : "unknown";
  const siteTone: Tone = live ? "ok" : liveQuery.isError ? "down" : "unknown";
  // Internal services = the backend key behind rate-limiting, reward texts
  // and alert emails. Missing key = those silently degrade, so flag red.
  const serviceKeyTone: Tone = live
    ? live.service_key === "ok"
      ? "ok"
      : "down"
    : "unknown";
  const redeemErrors = recentErrors.filter(
    (e) => e.function_name === "activate-redeem",
  ).length;
  const paymentErrors = recentErrors.filter(
    (e) => e.function_name === "payments-webhook",
  ).length;
  const redeemTone: Tone =
    redeemErrors > 3 ? "degraded" : live ? "ok" : "unknown";
  const paymentTone: Tone =
    paymentErrors > 3 ? "degraded" : live ? "ok" : "unknown";

  // Top-line tone = worst of the five.
  const allTones: Tone[] = [siteTone, dbTone, serviceKeyTone, redeemTone, paymentTone];
  const overall: Tone = allTones.includes("down")
    ? "down"
    : allTones.includes("degraded")
      ? "degraded"
      : allTones.includes("unknown")
        ? "unknown"
        : "ok";

  const overallLabel =
    overall === "ok"
      ? "All systems operational"
      : overall === "degraded"
        ? "Partial degradation"
        : overall === "down"
          ? "Service disruption"
          : "Checking status…";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="px-4 py-6 border-b border-border">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" aria-label="Tack home" className="hover:opacity-80 transition-opacity">
            <TackLogo size="sm" />
          </Link>
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            ← Back to site
          </Link>
        </div>
      </header>

      <main className="flex-1 px-4 py-10">
        <div className="max-w-3xl mx-auto space-y-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              System status
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Live operational status for Tack and recent incident history.
            </p>
          </div>

          {/* Top-line status */}
          <section className="rounded-xl border border-border bg-card px-5 py-5">
            <div className="flex items-center gap-3">
              <StatusDot tone={overall} />
              <div className="flex-1">
                <h2 className="text-lg font-medium text-foreground">
                  {overallLabel}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {live
                    ? `Last checked ${formatRelative(live.timestamp)} · response in ${live.latency_ms} ms`
                    : liveQuery.isError
                      ? "Could not reach health endpoint"
                      : "Checking…"}
                </p>
              </div>
            </div>
          </section>

          {/* Components */}
          <section className="rounded-xl border border-border bg-card px-5 py-2">
            <ComponentRow
              label="Public site"
              tone={siteTone}
              detail={
                siteTone === "ok"
                  ? "Operational"
                  : siteTone === "down"
                    ? "Unreachable"
                    : "—"
              }
            />
            <ComponentRow
              label="Database"
              tone={dbTone}
              detail={
                dbTone === "ok"
                  ? "Operational"
                  : dbTone === "down"
                    ? "Connection error"
                    : "—"
              }
            />
            <ComponentRow
              label="Reward texts & alerts"
              tone={serviceKeyTone}
              detail={
                serviceKeyTone === "ok"
                  ? "Operational"
                  : serviceKeyTone === "down"
                    ? "Configuration error"
                    : "—"
              }
            />
            <ComponentRow
              label="Customer redemptions"
              tone={redeemTone}
              detail={
                redeemTone === "degraded"
                  ? `${redeemErrors} errors / 15 min`
                  : redeemTone === "ok"
                    ? "Operational"
                    : "—"
              }
            />
            <ComponentRow
              label="Payments"
              tone={paymentTone}
              detail={
                paymentTone === "degraded"
                  ? `${paymentErrors} errors / 15 min`
                  : paymentTone === "ok"
                    ? "Operational"
                    : "—"
              }
            />
          </section>

          {/* 30-day history */}
          <section className="rounded-xl border border-border bg-card px-5 py-5">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-medium text-foreground">
                Last 30 days
              </h3>
              <p className="text-xs text-muted-foreground">
                {uptime !== null
                  ? `${uptime.toFixed(2)}% uptime`
                  : "Collecting uptime data…"}
                {incidents.length > 0 && (
                  <>
                    {" · "}
                    {incidents.length} {incidents.length === 1 ? "incident" : "incidents"}
                  </>
                )}
              </p>
            </div>

            <div className="mt-4 flex items-end gap-[3px] h-10">
              {dayBuckets.map((b, i) => {
                const tone: Tone = !b.hasData
                  ? "unknown"
                  : b.bad === 0
                    ? "ok"
                    : b.bad / b.total > 0.1
                      ? "down"
                      : "degraded";
                const color =
                  tone === "ok"
                    ? "bg-emerald-500/80"
                    : tone === "degraded"
                      ? "bg-amber-500/80"
                      : tone === "down"
                        ? "bg-destructive/80"
                        : "bg-muted-foreground/20";
                return (
                  <div
                    key={i}
                    className={cn("flex-1 rounded-sm h-full", color)}
                    title={`${formatDate(b.dayStart.toISOString())} — ${b.total} checks, ${b.bad} non-ok`}
                  />
                );
              })}
            </div>

            <div className="mt-2 flex justify-between text-[11px] text-muted-foreground">
              <span>30 days ago</span>
              <span>today</span>
            </div>
          </section>

          {/* Incidents */}
          <section className="rounded-xl border border-border bg-card px-5 py-5">
            <h3 className="text-sm font-medium text-foreground">
              Recent incidents
            </h3>
            {incidents.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                {hasEnoughHistory
                  ? "No incidents recorded in the last 30 days."
                  : "History starts from first recorded check — no incidents yet."}
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {incidents.map((inc, i) => (
                  <li key={i} className="text-sm text-foreground">
                    <span className="text-muted-foreground">
                      {formatDate(inc.startedAt.toISOString())} —
                    </span>{" "}
                    Brief degradation ({inc.durationMin} min) — resolved
                  </li>
                ))}
              </ul>
            )}
          </section>

          <p className="text-xs text-muted-foreground text-center">
            Status data refreshes every 30 seconds. Probes run from inside our
            infrastructure; for outside-in monitoring we additionally rely on
            external uptime services.
          </p>

          <SentrySmokeTest />
        </div>
      </main>

      <SiteFooter variant="legal" />
    </div>
  );
}

// Hidden Sentry smoke-test trigger. Renders only when ?sentry-test=1 is present.
// Used to verify that production events reach the configured Sentry project.
function SentrySmokeTest() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get("sentry-test") !== "1") return null;

  const configured = isSentryConfigured();
  const initialized = isSentryInitialized();

  return (
    <div className="rounded-xl border border-dashed border-border bg-muted/30 px-5 py-4 text-center">
      <p className="text-xs text-muted-foreground mb-3">
        Sentry smoke test — events should appear in Sentry within ~30s.
      </p>
      <p className="mb-3 text-xs text-muted-foreground">
        SDK configured: {configured ? "yes" : "no"} · initialized: {initialized ? "yes" : "no"}
      </p>
      <div className="flex justify-center gap-2">
        <button
          type="button"
          onClick={() => {
            Sentry.captureMessage(
              `Sentry smoke test (message) — ${new Date().toISOString()}`,
              "info",
            );
          }}
          className="inline-flex items-center justify-center rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          Send test message
        </button>
        <button
          type="button"
          onClick={() => {
            Sentry.captureException(
              new Error(`Sentry smoke test (error) — ${new Date().toISOString()}`),
            );
          }}
          className="inline-flex items-center justify-center rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:bg-destructive/90"
        >
          Throw test error
        </button>
      </div>
    </div>
  );
}
